
import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import Gender from "../../enums/gender.enum"
import { useState, useContext, useRef, useEffect } from "react"
import { UserContext } from "../../contexts/user.context"
import ProductTypeCode from "../../enums/productTypeCode.enum"
import { Vo2MaxIntro } from "./vo2maxIntro.component.js"
import { ConfirmVo2max } from "./confirmVo2max.component.js"
import { listProductTypes } from '../../services/productType.service'
import MembershipTypeCode from "../../enums/membershipTypeCode.enum.js"
import { getMembershipTypeByCode} from '../../services/membershipType.service'
import { completeFlow } from "../../services/flow.service.js"
import { useNavigate } from "react-router-dom"
import ProductHelper from "../../helpers/product.helper"
import UserHelper from "../../helpers/user.helper"

export const Step = {
  INTRO: 'intro',
  LOCATION: 'location',
  FACILITY: 'facility',
  BASIC_INFO: 'basic-info',
  CONTACT: 'contact',
  PAY: 'pay',
  PASSWORD: 'password',
  CONFIRM: 'confirm',
}

export const Vo2maxFlow = () => {
  const { instalabMembership, currentUser } = useContext(UserContext)
  const [flow, setFlow] = useState()
  const [skeleton, setSkeleton] = useState()
  const [productTypes, setProductTypes] = useState()
  const [hasPass, setHasPass] = useState()
  const hasCompletedFlowRef = useRef(false)
  const [membershipType, setMembershipType] = useState()
  const [facilityPrice, setFacilityPrice] = useState()

  const [initialUser, setInitialUser] = useState()

  useEffect(() => {
    fetchSkeleton()
  }, [hasPass, membershipType, productTypes, initialUser, instalabMembership, flow])

  useEffect(() => {
    fetchProductTypes()
    fetchMembershipType()
  }, [])

  useEffect(() => {
    if (flow?.facility && flow.facility.menu) {
      const product = flow.facility.menu.find(f => f.productType === getProductType(ProductTypeCode.VO2MAX)?._id);
      if (product && product.price) {
        setFacilityPrice(product.price);
      }
    }
  }, [flow, productTypes]);

  const fetchProductTypes = async () => {
    const response = await listProductTypes({
      filter: {
        code: {
          $in: [
            ProductTypeCode.VO2MAX,
          ]
        }
      }
    })
    setProductTypes(response)
  }

  const fetchMembershipType = async () => {
    const response = await getMembershipTypeByCode(MembershipTypeCode.LONGEVITY)
    setMembershipType(response)
  }

  const getProductType = (code) => {
    return productTypes?.find(productType => productType.code === code)
  }

  const instructions = [
    {
      sections: [{
        title: 'Schedule Your Vo2 Max',
        description: <>Instalab's concierge team will reach out to you to schedule your appointment with a nearby performance center.</>
      }, 
    ]
    }, 
    {
      sections: [{
        title: 'Get Test Done',
        description: <>Wear comfortable clothing and running shoes. The test should take about 20-40 minutes depending on your current endurance level.</>
      }]
    },
    {
      sections: [{
        title: 'Review Test Results',
        description: <>You can expect to receive your results within 24 hours. Instalab physicians are available to review your results with you and provide recommendations.</>
      }]
    }]

  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser || !productTypes) return
    const hasCredit = UserHelper.hasCredit(initialUser, ProductTypeCode.VO2MAX)

    setSkeleton({
      [Step.INTRO]: {
        type: StepType.CUSTOM,
        nextStep: Step.LOCATION,
        content: ({ footer }) => <Vo2MaxIntro footer={footer} instructions={instructions}/>
        
      },
      // [Step.ACCOUNT]: {
      //   type: StepType.MULTIPLE_INPUT,
      //   nextStep: Step.LOCATION,
      //   buildUser: true,
      //   title: `First, let's create your Instalab acccount.`,
      //   description: <>Already have an account? <a className="secondary-link" onClick={() => navigate(`/login?redirect=/flow/${FlowType.VO2MAX}`)}>Log in</a>.</>, 
      //   fields: [{
      //     name: 'email',
      //     label: 'Email Address',
      //     placeholder: 'Type your email here...',
      //     email: true,
      //     required: true,
      //     unique: true,
      //   }, {
      //     name: 'password',
      //     label: 'Password',
      //     placeholder: 'Type your password here...',
      //     password: true,
      //     required: true,
      //   }],
      //   skipIf: () => {
      //     return hasPass
      //   }
      // },

      [Step.LOCATION]: {
        type: StepType.LOCATION,
        buildUser: true,
        buildProduct:true,
        nextStep: Step.FACILITY,
        title: `What's your current address?`,
        description: `We'll search for performance labs that offer Vo2Max near this address.`,
        field: {
          name: 'location'
        },
      },
      [Step.FACILITY]: {
        type: StepType.FACILITY,
        nextStep: Step.PAY,
        buildFlow: true,
        productTypeCode: ProductTypeCode.VO2MAX,
        showPrice: !hasCredit,
        title: `Choose your preferred location.`,
        description: `We partner with top-rated performance labs. Select the one that works best for you.`,
        field: {
          name: 'facility',
          required: true,
        },
        onSuccess: (_, flow) => {
          setFacilityPrice(flow?.facility?.menu?.find(f => f.productType === getProductType(ProductTypeCode.VO2MAX)?._id)?.price)
        }
      },

      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.BASIC_INFO,
        addProduct: true,
        showPrice: !hasCredit,
        priceOverride: [{
          code: ProductTypeCode.VO2MAX,
          price: facilityPrice
        }],
        productType: ProductTypeCode.VO2MAX,
        title: () => {

          const totalCost = ProductHelper.getTotalCostV2({
            productTypes: [getProductType(ProductTypeCode.VO2MAX)],
            currentUser,
            instalabMembership,
            priceOverride: [{
              code: ProductTypeCode.VO2MAX,
              price: facilityPrice
            }]
          })
          return <>Pay <span className="true-price">${totalCost}</span> for the <span className="product-name">VO2Max</span>.</>
        },
        description: "This includes the test and interpretation of results. Our concierge team will reach out to schedule your scan within 1 business day.",
        skipIf: () => {
          return  hasCredit
        }
      },


      [Step.BASIC_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.CONTACT,
        buildUser: true,
        title: `Let's get to know you.`,
        description: `We just need a few details to prepare your order and book your appointment at ${flow?.facility?.name}.`,
        fields: [
          {
            type: "group",
            label: "Full Name",
            required: true,
            fields: [
              {
                name: 'firstName',
                placeholder: 'First Name',
                label: 'First Name',
                required: true,
                type: 'text',
              },  
              {
                name: 'lastName',
                placeholder: 'Last Name',
                label: 'Last Name',
                required: true,
                type: 'text',
              },
            ]
          },
          {
            name: 'dob',
            label: 'Date of Birth',
            required: true,
            type: 'date',
            placeholder: 'MM/DD/YYYY',
          },
          {
            name: 'gender',
            label: 'Biological Sex',
            required: true,
            type: 'select',
            multiple:false,
            options: [
              {
                label: 'Male',
                value: Gender.MALE,
              }, {
                label: 'Female',
                value: Gender.FEMALE,
              }
            ],
          },
        ],
        skipIf: () => {
          return hasPass && (initialUser?.gender && initialUser?.dob && initialUser?.firstName && initialUser?.lastName)
        }
      },

      [Step.CONTACT]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PASSWORD,
        buildUser: true,
        title: `How can we reach you?`,
        description: "We’ll send your appointment confirmation, directions and results here.",
        fields: [
          {
          name: 'email',
          label: 'Email Address',
          required: true,
          type: 'email',
          unique: true,
        }, {
          name: 'phone',
          label: 'Phone Number',
          required: true,
          type: 'phone',
        }],
        skipIf: () => {
          return hasPass && (initialUser?.email && initialUser?.phone)
        }
      },

          
      [Step.PASSWORD]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CONFIRM,
        buildUser: true,
        title: `Create your password.`,
        description: `This lets you securely access your results, manage appointments, and track your health over time.`,
        field: {
          name: 'password',
          placeholder: 'Enter your password',
          required: true,
          password: true,
        },
        skipIf: () => {
          return hasPass
        },
      },


      [Step.CONFIRM]: {
        type: StepType.STATIC,
        showFooter: false,
        title: `All set!`,
        description: `We'll reach out to you to schedule your appointment within 1 business day.`,
        enterStyle: { display: 'none' },
        buttonStyle: { display: 'none' },
        content: <ConfirmVo2max instructions={instructions}/>,
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },


    })
  }

  return <>
    <Flow 
      skeleton={skeleton} 
      flow={flow}
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
      // productTypeCode={ProductTypeCode.VO2MAX}
    />


  </>
}